import { SUPPORTED_LANGUAGES } from "@/internals/constants/language";
import type { LanguageCode } from "@/types/languages";

export const SHORT_LOCALE_MAP: Record<string, LanguageCode> = {
  en: "en-US",
  de: "de-DE",
  es: "es-ES",
  fr: "fr-FR",
  it: "it-IT",
  ja: "ja-JP",
  ko: "ko-KR",
  nl: "nl-NL",
  pt: "pt-BR",
  zh: "zh-TW",
  pl: "pl-PL",
  sv: "sv-SE",
};

export const isSupportedLanguage = (locale?: string | null): locale is LanguageCode => {
  if (!locale) return false;
  return SUPPORTED_LANGUAGES.includes(locale as LanguageCode);
};

// Normalizes and matches a locale string to a supported LanguageCode.
// Handles short locales (en → en-US), underscores, case variations.
export const matchSupportedLocale = (value?: string | null): LanguageCode | null => {
  if (!value) return null;

  const normalized = value.trim().replaceAll("_", "-").toLowerCase();
  if (!normalized) return null;

  if (SHORT_LOCALE_MAP[normalized]) {
    return SHORT_LOCALE_MAP[normalized];
  }

  const [lang, region] = normalized.split("-");
  if (!region) return SHORT_LOCALE_MAP[lang] ?? null;

  const candidate = `${lang}-${region.toUpperCase()}`;
  return isSupportedLanguage(candidate) ? (candidate as LanguageCode) : null;
};

// Strips locale segment from pathname, returns the base path.
export const stripLocaleFromPath = (pathname?: string | null): string => {
  if (!pathname || pathname === "/") return "/";

  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return "/";

  const locale = matchSupportedLocale(segments[0]);
  if (!locale) return pathname;

  const rest = segments.slice(1).join("/");
  return rest ? `/${rest}` : "/";
};

// Builds a path with the locale prefix.
export const buildPathWithLocale = (locale: LanguageCode, pathname?: string | null): string => {
  const basePath = stripLocaleFromPath(pathname);

  return basePath === "/" ? `/${locale}` : `/${locale}${basePath}`;
};

export const normalizeLocale = (locale: LanguageCode): string => {
  const entry = Object.entries(SHORT_LOCALE_MAP).find(([, value]) => value === locale);
  return entry?.[0] ?? "en";
};
